const TECHNICAL_NUMERIC_LENGTH = 14
const PHONE_LIKE_NUMERIC_LENGTH = 8

const trimString = (value) => String(value || '').trim()

const normalizeDigits = (value) => trimString(value).replace(/\D+/g, '')

const normalizeComparableText = (value) => trimString(value)
	.normalize('NFD')
	.replace(/\p{Diacritic}/gu, '')
	.toLowerCase()

const getConversationIds = (conversation) => {
	const ids = [
		conversation?.id,
		conversation?._id,
		conversation?.remoteJid,
		conversation?.remote_jid,
		...(Array.isArray(conversation?.alternateIds) ? conversation.alternateIds : []),
	]

	return Array.from(new Set(ids.map(trimString).filter(Boolean)))
}

const isLidConversationId = (value) => trimString(value).toLowerCase().includes('@lid')

const hasTechnicalConversationId = (conversation) => {
	return getConversationIds(conversation).some((id) => {
		return isTechnicalIdentifier(id) || isLidConversationId(id)
	})
}

const getHumanConversationName = (conversation) => {
	const name = trimString(conversation?.kwik_contact_name || conversation?.name)
	return isHumanReadableName(name) ? normalizeComparableText(name) : ''
}

const hasDeletedPreview = (conversation) => {
	const message = conversation?.message
	const messageText = typeof message === 'string'
		? message
		: typeof message?.message === 'string'
		? message.message
		: ''

	return conversation?.message_type === 'protocolMessage'
		|| conversation?.message_type === 'editedMessage'
		|| message?.type === 'DELETED'
		|| normalizeComparableText(messageText).includes('mensagem apagada')
}

const getConversationMergeKey = (conversation) => {
	if (!conversation) {
		return ''
	}

	if (conversation?.type === 'GROUP') {
		return `group:${trimString(conversation?.id || conversation?.remoteJid || conversation?.remote_jid)}`
	}

	const kwikContactId = trimString(conversation?.kwik_contact_id)
	if (kwikContactId) {
		return `kwik:${kwikContactId}`
	}

	const name = getHumanConversationName(conversation)
	if (name) {
		return `name:${name}`
	}

	const ids = getConversationIds(conversation)
	const phoneId = ids.find((id) => id.includes('@s.whatsapp.net') || id.includes('@c.us'))
	const phoneDigits = normalizeDigits(phoneId || conversation?.phone_num)
	if (phoneDigits && !ids.every(isLidConversationId)) {
		return `phone:${phoneDigits}`
	}

	const picture = trimString(conversation?.profile_picture || conversation?.avatarUrl)
	if (name && picture) {
		return `profile:${name}:${picture}`
	}

	if (name && (hasTechnicalConversationId(conversation) || hasDeletedPreview(conversation))) {
		return `name:${name}`
	}

	return ids[0] ? `id:${ids[0]}` : ''
}

const preferConversationId = (ids = []) => {
	return ids.find((id) => !isLidConversationId(id)) || ids[0] || ''
}

const mergeConversationPair = (current, incoming) => {
	if (!current) {
		const ids = getConversationIds(incoming)
		return {
			...incoming,
			id: preferConversationId(ids) || incoming?.id,
			_id: preferConversationId(ids) || incoming?._id || incoming?.id,
			alternateIds: ids,
		}
	}

	const ids = Array.from(new Set([...getConversationIds(current), ...getConversationIds(incoming)]))
	const preferredId = preferConversationId(ids) || current?.id || incoming?.id
	const currentTimestamp = Number(current?.last_message_timestamp || 0)
	const incomingTimestamp = Number(incoming?.last_message_timestamp || 0)
	const latest = incomingTimestamp >= currentTimestamp ? incoming : current
	const oldest = latest === incoming ? current : incoming

	return {
		...oldest,
		...latest,
		id: preferredId,
		_id: preferredId,
		alternateIds: ids,
		name: trimString(current?.kwik_contact_name || current?.name) || trimString(incoming?.kwik_contact_name || incoming?.name) || latest?.name,
		kwik_contact_name: trimString(current?.kwik_contact_name) || trimString(incoming?.kwik_contact_name) || latest?.kwik_contact_name,
		profile_picture: latest?.profile_picture || oldest?.profile_picture || '',
		avatarUrl: latest?.avatarUrl || latest?.profile_picture || oldest?.avatarUrl || oldest?.profile_picture || '',
	}
}

export const mergeDuplicateConversations = (conversations = []) => {
	const mergedByKey = new Map()
	const passthrough = []

	conversations.forEach((conversation) => {
		const mergeKey = getConversationMergeKey(conversation)
		if (!mergeKey) {
			passthrough.push(conversation)
			return
		}

		mergedByKey.set(mergeKey, mergeConversationPair(mergedByKey.get(mergeKey), conversation))
	})

	return [...mergedByKey.values(), ...passthrough].sort((first, second) => {
		return Number(second?.last_message_timestamp || 0) - Number(first?.last_message_timestamp || 0)
	})
}

export const isTechnicalIdentifier = (value) => {
	const normalized = trimString(value)
	if (!normalized) {
		return false
	}

	if (normalized.includes('@lid') || normalized.includes('@s.whatsapp.net') || normalized.includes('@g.us')) {
		return true
	}

	if (normalized.includes('@')) {
		return false
	}

	const digits = normalizeDigits(normalized)
	return digits === normalized && digits.length >= TECHNICAL_NUMERIC_LENGTH
}

export const isPhoneLikeIdentifier = (value) => {
	const normalized = trimString(value)
	if (!normalized || normalized.includes('@')) {
		return false
	}

	const digits = normalizeDigits(normalized)
	return digits.length >= PHONE_LIKE_NUMERIC_LENGTH && /^[+\d\s().-]+$/.test(normalized)
}

export const isHumanReadableName = (value) => {
	const normalized = trimString(value)
	if (!normalized) {
		return false
	}

	if (isTechnicalIdentifier(normalized) || isPhoneLikeIdentifier(normalized)) {
		return false
	}

	const digits = normalizeDigits(normalized)
	return digits !== normalized || digits.length < TECHNICAL_NUMERIC_LENGTH
}

export const isTechnicalConversation = (conversation) => {
	if (!conversation || conversation?.type === 'GROUP') {
		return false
	}

	const conversationId = trimString(conversation?.id || conversation?.remoteJid || conversation?.remote_jid)
	const conversationName = trimString(conversation?.name)
	const kwikContactName = trimString(conversation?.kwik_contact_name)

	if (isHumanReadableName(kwikContactName) || isHumanReadableName(conversationName)) {
		return false
	}

	return isTechnicalIdentifier(conversationId) || isTechnicalIdentifier(conversationName)
}

export const getVisibleConversations = (conversations = []) => {
	const mergedConversations = mergeDuplicateConversations(conversations)
	const technicalConversations = mergedConversations.filter(isTechnicalConversation)
	if (!technicalConversations.length) {
		return mergedConversations
	}

	const humanConversations = mergedConversations.filter((conversation) => !isTechnicalConversation(conversation))
	return humanConversations.length ? humanConversations : mergedConversations
}
